// src/features/MediaLibrary/AudioBrowser.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AudioService from './AudioService';
import AudioPlayer from './AudioPlayer';
import './MediaBrowser.css';

const AudioBrowser = ({ 
  onSelectAudio, 
  onUploadComplete,   // NEW: called after upload so parent can auto-confirm
  selectedAudioId, 
  showSearchBar = true 
}) => {
  const [audio, setAudio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isDeleting, setIsDeleting] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [displayCount, setDisplayCount] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 10;

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileInputRef = useRef(null);

  const fetchAudio = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const audioList = await AudioService.getAudioAssets();
      setAudio(audioList);
      setTotalCount(audioList.length);
    } catch (err) {
      console.error('Error fetching audio:', err);
      setError('Failed to load audio files');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudio();
  }, [fetchAudio]);

  const handleRefresh = () => {
    fetchAudio();
    setMessage({ type: 'success', text: 'Audio library refreshed' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleLoadMore = () => setDisplayCount(prev => prev + ITEMS_PER_PAGE);
  const handleSearch = (value) => setSearchTerm(value);
  const handleAudioClick = (audioItem) => { if (onSelectAudio) onSelectAudio(audioItem); };

  // Upload handler
  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    setUploadProgress('Uploading...');

    try {
      const newAsset = await AudioService.uploadAudio(uploadFile, {
        title: uploadFile.name.replace(/\.[^/.]+$/, ''), // filename without extension as title
      });

      setAudio(prev => [newAsset, ...prev]);
      setTotalCount(prev => prev + 1);
      setMessage({ type: 'success', text: `"${uploadFile.name}" uploaded successfully` });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);

      // Reset upload area
      setUploadFile(null);
      setShowUpload(false);
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Notify parent of upload completion (for auto-confirm in MediaSelector)
      // and also select the file in the browser list
      if (onUploadComplete) {
        onUploadComplete(newAsset);
      } else if (onSelectAudio) {
        onSelectAudio(newAsset);
      }

    } catch (err) {
      console.error('Upload error:', err);
      setMessage({ type: 'error', text: 'Upload failed. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleDelete = async (audioId, event) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this audio file?')) return;
    
    setIsDeleting(audioId);
    try {
      const audioItem = audio.find(item => item.id === audioId);
      if (!audioItem) throw new Error('Audio file not found');
      await AudioService.deleteAudioAsset(audioItem);
      setAudio(prev => prev.filter(item => item.id !== audioId));
      setMessage({ type: 'success', text: 'Audio file deleted successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting audio:', error);
      setMessage({ type: 'error', text: 'Failed to delete audio file' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setIsDeleting(null);
    }
  };

  const openPreview = (audioItem, event) => { event.stopPropagation(); setShowPreview(audioItem); };
  const closePreview = () => setShowPreview(null);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getSafeDate = (dateValue) => {
    if (!dateValue) return 'Unknown';
    try {
      if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
      if (dateValue instanceof Date) return dateValue.toLocaleDateString();
      if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString();
      return 'Unknown';
    } catch { return 'Unknown'; }
  };

  const filteredAudio = audio.filter(item => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (item.name && item.name.toLowerCase().includes(s)) ||
      (item.title && item.title.toLowerCase().includes(s)) ||
      (item.description && item.description.toLowerCase().includes(s))
    );
  });

  const paginatedAudio = filteredAudio.slice(0, displayCount);
  const hasMore = filteredAudio.length > displayCount;

  return (
    <div className="media-browser">
      <div className="browser-header">
        <h3>Browse Audio ({totalCount}+ files)</h3>
        <div className="browser-actions">
          <button
            onClick={() => setShowUpload(v => !v)}
            style={{
              padding: '6px 14px',
              backgroundColor: showUpload ? '#19a4a0' : '#f1f1f1',
              color: showUpload ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '8px',
              fontWeight: '500'
            }}
          >
            {showUpload ? 'Cancel Upload' : '+ Upload New'}
          </button>
          <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {/* Upload panel */}
      {showUpload && (
        <div style={{
          margin: '10px 0',
          padding: '16px',
          backgroundColor: '#f0fafa',
          border: '1px solid #19a4a0',
          borderRadius: '8px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#19a4a0' }}>Upload Audio File</h4>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            style={{ display: 'block', marginBottom: '10px' }}
          />
          {uploadFile && (
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '10px' }}>
              Selected: <strong>{uploadFile.name}</strong> ({formatFileSize(uploadFile.size)})
            </div>
          )}
          <button
            onClick={handleUpload}
            disabled={!uploadFile || uploading}
            style={{
              padding: '8px 20px',
              backgroundColor: uploadFile && !uploading ? '#19a4a0' : '#aaa',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: uploadFile && !uploading ? 'pointer' : 'not-allowed',
              fontWeight: '500'
            }}
          >
            {uploading ? uploadProgress : 'Upload & Select'}
          </button>
          <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
            After uploading, the file will be automatically selected for this question.
          </p>
        </div>
      )}

      {message.text && (
        <div className={`message-banner ${message.type}`}>
          {message.text}
        </div>
      )}

      {showSearchBar && (
        <div className="search-container">
          <input
            type="text"
            placeholder="Search audio files..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="search-input"
          />
        </div>
      )}

      {error && <div className="error-container">{error}</div>}
      {loading && <div className="loading-container">Loading audio files...</div>}

      {!loading && paginatedAudio.length === 0 && (
        <div className="no-items-message">
          {audio.length === 0 ? 'No audio files found. Upload some audio to get started.' : 'No audio files match your search.'}
        </div>
      )}

      {!loading && paginatedAudio.length > 0 && (
        <>
          <div className="media-grid">
            {paginatedAudio.map(item => {
              const displayName = item.title || item.name || 'Untitled Audio';
              const fileSize = formatFileSize(item.size);
              const uploadDate = getSafeDate(item.createdAt);
              const isSelected = selectedAudioId === item.id;
              
              return (
                <div
                  key={item.id}
                  className={isSelected ? 'media-item selected' : 'media-item'}
                  onClick={() => handleAudioClick(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="media-item-header">
                    <div className="media-name" title={displayName}>{displayName}</div>
                    <button
                      className="delete-btn"
                      onClick={(e) => handleDelete(item.id, e)}
                      disabled={isDeleting === item.id}
                      title="Delete audio file"
                    >
                      {isDeleting === item.id ? '...' : 'x'}
                    </button>
                  </div>
                  
                  <div className="media-preview">
                    <div style={{ 
                      padding: '10px',
                      backgroundColor: '#e3f2fd',
                      textAlign: 'center',
                      minHeight: '80px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '5px' }}>♪</div>
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '5px' }}>{fileSize}</div>
                      <AudioPlayer 
                        audioId={item.id}
                        showFileName={false}
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                      />
                    </div>
                    
                    <button
                      onClick={(e) => openPreview(item, e)}
                      title="Preview audio"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'rgba(255,255,255,0.9)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ...
                    </button>
                  </div>
                  
                  <div className="media-details">
                    <span>{item.mimeType || 'audio/mpeg'}</span>
                    <span>{fileSize}</span>
                  </div>
                  
                  <div className="media-meta">
                    <span className="upload-date">{uploadDate}</span>
                    {item.tags && Array.isArray(item.tags) && item.tags.length > 0 && (
                      <span className="tags-preview">{item.tags.length} tags</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '20px', padding: '20px' }}>
              <button
                onClick={handleLoadMore}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Load More Audio ({ITEMS_PER_PAGE} more)
              </button>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                Showing {paginatedAudio.length} of {filteredAudio.length} audio files
              </div>
            </div>
          )}
        </>
      )}

      {showPreview && (
        <div className="preview-modal" onClick={closePreview}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closePreview}>x</button>
            <h3>{showPreview.title || showPreview.name || 'Audio Preview'}</h3>
            <div className="preview-media">
              <div style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>♪</div>
                <AudioPlayer audioId={showPreview.id} showFileName={true} />
              </div>
            </div>
            <div className="preview-details">
              <p><strong>Size:</strong> {formatFileSize(showPreview.size)}</p>
              <p><strong>Type:</strong> {showPreview.mimeType || 'audio/mpeg'}</p>
              {showPreview.title && <p><strong>Title:</strong> {showPreview.title}</p>}
              {showPreview.description && <p><strong>Description:</strong> {showPreview.description}</p>}
              {showPreview.tags && Array.isArray(showPreview.tags) && showPreview.tags.length > 0 && (
                <p><strong>Tags:</strong> {showPreview.tags.join(', ')}</p>
              )}
              <p><strong>Uploaded:</strong> {getSafeDate(showPreview.createdAt)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioBrowser;